import TimeRange from "./TimeRange";
import {VisibilityRestriction} from "./ProjectDetails";
import fuzzysort from "fuzzysort";
import { BaseDeviceInfo } from "./BaseDeviceInfo";
import { shouldHideSerialNumber } from "../repository/ClientRepository";

export interface ClientPanel extends BaseDeviceInfo {
  currentProjectId: string | null;
  currentProjectInternalNumber: number | null;
  currentProjectName: string | null;
  currentProjectNumber: string | null;
  currentProjectAssignmentTimeRange?: TimeRange | null;
  currentProjectContactPersonNumber: number | null;
  currentProjectContactPersonName: string | null;
  currentProjectVisibilityRestrictions?: VisibilityRestriction[] | null;

  currentProjectResponsibleUserIds?: string[] | null;
  currentProjectBranch?: string | null;

  branch: string | null;

  panelCurrentlyAssignedToClient?: boolean;

  lastProjectId: string | null;
  lastProjectName: string | null;
  lastProjectNumber: string | null;
  lastProjectAssignmentTimeRange: TimeRange | null;

  lastProjectContactPersonNumber: number | null;
  lastProjectContactPersonName: string | null;

  serialNumber: string;

  isTemporarilyAssigned?: boolean;
  wasAssignedToClient?: boolean;
  flaggedAsDeleted?: boolean;
}

export default ClientPanel;

/**
 * Whether the record is one of the client's current boxes: assigned to the client now and
 * not deleted by IRES. The same rule `getAvailablePanels()` queries with.
 *
 * A box that moves to another client leaves its record here, still holding its last
 * inventory number. It is not the client's device any more, so it must not block the
 * numbers the client gives to the boxes it has now.
 */
export function isCurrentClientPanel(
  clientPanel: Pick<ClientPanel, "panelCurrentlyAssignedToClient" | "flaggedAsDeleted">
) {
  return clientPanel.panelCurrentlyAssignedToClient === true && clientPanel.flaggedAsDeleted !== true;
}

export interface ClientPanelExtended extends ClientPanel {
  id?: string;
}

export function getClientPanelName(clientPanel: Pick<ClientPanel, 'inventoryNumber' | 'serialNumber'>) {
    const inventoryNumber = clientPanel.inventoryNumber;
    const serialNumber = clientPanel.serialNumber;

    const serialNumberPostfix =
        serialNumber && serialNumber !== inventoryNumber && !shouldHideSerialNumber()
          ? ` / ${serialNumber}` 
          : "";

    return inventoryNumber + serialNumberPostfix;
}

function findClientPanelByInventoryNumberFuzzy(
    searchText: string,
    clientPanels: ClientPanel[]
) {
    const inventoryNumbers: string[] = [];
    const clientPanelsWithInventoryNumber: ClientPanel[] = [];

    clientPanels.forEach((clientPanel) => {
        if (clientPanel.inventoryNumber && clientPanel.inventoryNumber !== "") {
            inventoryNumbers.push(clientPanel.inventoryNumber);
            clientPanelsWithInventoryNumber.push(clientPanel);
        }
    });

    const result = fuzzysort.go(searchText, inventoryNumbers, {
        allowTypo: false,
    });

    if (result.length !== 1) {
        return undefined;
    }

    const index = inventoryNumbers.indexOf(result[0].target);

    return clientPanelsWithInventoryNumber[index];
}

export enum ElectricityMeterManufacturer {
    EASTRON = "EASTRON"
}

export function findClientPanel(
  searchText: string,
  clientPanels: ClientPanel[]
) {
  const searchTextLowerCase = searchText.toLowerCase();
  return (
    clientPanels.find(
      (clientPanel) =>
        //Search for exact match
        clientPanel.serialNumber.toLowerCase() === searchTextLowerCase ||
        clientPanel.inventoryNumber?.toLowerCase() === searchTextLowerCase
    ) ?? findClientPanelByInventoryNumberFuzzy(searchText, clientPanels)
  );
}

export function getClientPanelDisplayName(
  clientPanel: ClientPanel,
  divider?: string,
) {
  const serialNumber = clientPanel.serialNumber;
  const inventoryNumber = clientPanel.inventoryNumber;

  if (shouldHideSerialNumber()) {
    return inventoryNumber;
  }

  if (!inventoryNumber || inventoryNumber === "" || inventoryNumber === serialNumber) {
    return serialNumber;
  }

  return `${inventoryNumber}${divider ?? " / "}${serialNumber}`;
}